import React from 'react'

/**
 * Generic List component
 * should be able to receive children (component) as item to render when data is not empty
 * should be able to receive react component that rendered when data is empty
 * should be able to receive placeholder for while the data is fetched
 */

export type ListState = 'idle' | 'loading' | 'error' | 'success'

interface Props<D> {
  /** Generic indicating the item types to render as list of item */
  data: D[]
  /** The current state of the list */
  dataState: ListState
  /** The component to render when the list is state is "success" and not empty */
  renderItem: (item: D, index: number) => React.ReactNode
  /** The key for each item */
  listKey: (item: D) => string | number
  /** The component to render when the data is empty */
  emptyComponent?: React.ReactNode
  /** The component to render when the list state "error" */
  errorComponent?: React.ReactNode
  /** The component to render when the list state is "loading" */
  loadingComponent?: React.ReactNode
  /** ClassName props for the root element classes */
  className?: string
  /** ClassName props for the items */
  itemClassName?: string
  /** Before list content */
  beforeList?: React.ReactNode
  /** After list content */
  afterList?: React.ReactNode
  itemWrapperClassName?: string
  /** Custom renderer for the data array */
  customListRenderer?: (
    data: D[],
    renderItem: (item: D, index: number) => React.ReactNode,
    listKey: (item: D) => string | number
  ) => React.ReactNode
}

export const GenericList = <D extends unknown>({
  data,
  listKey,
  renderItem,
  emptyComponent,
  errorComponent,
  loadingComponent,
  className,
  itemClassName,
  beforeList,
  afterList,
  itemWrapperClassName,
  customListRenderer,
  dataState = 'idle',
}: Props<D>) => {
  return (
    <div className={className}>
      {data?.length > 0 && !!beforeList ? beforeList : null}
      {(() => {
        let renderedComponent
        switch (dataState) {
          default:
          case 'idle':
          case 'loading':
            renderedComponent = (
              <div data-testid="loading-list">{loadingComponent}</div>
            )
            break
          case 'error':
            renderedComponent = (
              <div data-testid="error-list">{errorComponent}</div>
            )
            break
          case 'success':
            renderedComponent = (
              <div
                data-testid="success-list"
                className={
                  itemWrapperClassName ? itemWrapperClassName : undefined
                }
              >
                {data?.length > 0 ? (
                  customListRenderer ? (
                    customListRenderer(data, renderItem, listKey)
                  ) : (
                    data.map((item, index) => (
                      <div className={itemClassName} key={listKey(item)}>
                        {renderItem(item, index)}
                      </div>
                    ))
                  )
                ) : (
                  <div data-testid="empty-list">{emptyComponent}</div>
                )}
              </div>
            )
            break
        }
        return renderedComponent
      })()}
      {data?.length > 0 && !!afterList ? afterList : null}
    </div>
  )
}

export default GenericList
